"use client"

// React and Next.js imports
import React from "react"
import dynamic from "next/dynamic"

// Third-party library imports
import { useBaseBetslip } from "@azuro-org/sdk"
import { openModal } from "@locmod/modal"

// Local component imports
import { Button } from "components/inputs"
import { Media } from "components/layout"

// Local utility and hook imports
import { useTicket } from "@/contexts/TicketContext"
import useChainProviderHook from "@/hooks/useChainProvider"
import { useIsMounted } from "src/hooks"

// Local file imports
import messages from "./messages"
import { useDetailedSxBetslip } from "@/contexts/DetailedSxBetslipContext"

const BetslipModal = dynamic(
  () => import("./components/BetslipModal/BetslipModal")
)

const MobileBetslipButton: React.FC = () => {
  const { isOvertimeChain, isSXChain, isAzuroChain } = useChainProviderHook()
  const isMounted = useIsMounted()

  const { items } = useBaseBetslip()
  const { ticket } = useTicket()
  const { ticket: sxTicket } = useDetailedSxBetslip()

  // Badge component for displaying item count
  const Badge: React.FC<{ count: number }> = ({ count }) => (
    <div className="bg-accent-grey-30 text-caption-13 font-semibold px-1 min-w-8 h-8 ml-2 text-accent-grey-900 rounded-min flex items-center justify-center -mr-3">
      {count}
    </div>
  )

  // Determine right node based on chain and item count
  const rightNode = (() => {
    switch (true) {
      case isOvertimeChain && ticket.length > 0: {
        return <Badge count={ticket.length} />
      }
      case isAzuroChain && items.length > 0: {
        return <Badge count={items.length} />
      }
      case isSXChain && sxTicket.length > 0: {
        return <Badge count={sxTicket.length} />
      }
      default: {
        return undefined
      }
    }
  })()

  // Show skeleton until component is mounted to prevent hydration mismatch
  if (!isMounted) {
    return (
      <div className="animate-pulse">
        <div className="h-10 w-32 bg-accent-grey-700 rounded" />
      </div>
    )
  }

  return (
    <div>
      <Button
        title={messages.title}
        style="primary"
        leftIcon="interface/betslip"
        rightNode={rightNode}
        size={40}
        onClick={() => openModal("BetslipModal")}
      />
      <Media narrow mobile>
        <BetslipModal />
      </Media>
    </div>
  )
}

export default MobileBetslipButton
